// Minimal IndexedDB-backed offline upload queue for field captures.
import { api } from "@/lib/api";

const DB_NAME = "pi360-uploads";
const STORE = "queue";
let _db = null;

function openDb() {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => { _db = req.result; resolve(_db); };
    req.onerror = () => reject(req.error);
  });
}

function tx(mode) {
  return openDb().then((db) => db.transaction(STORE, mode).objectStore(STORE));
}

export async function enqueue(item) {
  const store = await tx("readwrite");
  const record = { id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, createdAt: Date.now(), ...item };
  return new Promise((resolve, reject) => {
    const r = store.add(record);
    r.onsuccess = () => resolve(record);
    r.onerror = () => reject(r.error);
  });
}

export async function getAll() {
  const store = await tx("readonly");
  return new Promise((resolve, reject) => {
    const r = store.getAll();
    r.onsuccess = () => resolve(r.result || []);
    r.onerror = () => reject(r.error);
  });
}

async function remove(id) {
  const store = await tx("readwrite");
  return new Promise((resolve) => {
    const r = store.delete(id);
    r.onsuccess = () => resolve();
    r.onerror = () => resolve();
  });
}

async function putRecord(record) {
  const store = await tx("readwrite");
  return new Promise((resolve) => {
    const r = store.put(record);
    r.onsuccess = () => resolve();
    r.onerror = () => resolve();
  });
}

let _flushing = false;

// Uploads all queued items. Bad items are retried up to 5 times then dropped,
// so a single corrupt blob never blocks the rest of the queue.
export async function flushQueue() {
  if (_flushing || !navigator.onLine) return 0;
  _flushing = true;
  let done = 0;
  try {
    const items = await getAll();
    for (const it of items) {
      try {
        const fd = new FormData();
        fd.append("file", it.blob, it.filename || "capture");
        if (it.fields) Object.entries(it.fields).forEach(([k, v]) => fd.append(k, v));
        await api.post(it.url, fd);
        await remove(it.id);
        done += 1;
      } catch (e) {
        if (!navigator.onLine) break; // still offline — stop, keep everything queued
        const attempts = (it.attempts || 0) + 1;
        if (attempts >= 5) await remove(it.id);
        else await putRecord({ ...it, attempts });
      }
    }
  } finally {
    _flushing = false;
  }
  return done;
}

export async function queueCount() {
  return (await getAll()).length;
}
